import React from "react";

/**
 * ProductPlaceholder — inline SVG rendered when a product has no real photo.
 * Looks like a branded cannabis package (jar / eighth-pack tin / vape box)
 * with the strain name printed on the "label". Pure SVG, never rotates,
 * loads instantly, varies by strain so adjacent cards look distinct.
 *
 * Per user directive: NEVER show a bud closeup unless we KNOW the strain
 * matches. This component renders only stylized packaging — no bud photos.
 */

// Deterministic djb2 hash for stable color/shape selection per strain.
const hashStr = (s) => {
  let h = 5381;
  const str = String(s || "");
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};

// Curated palette — premium cannabis-brand colors: deep forest, gold, plum,
// emerald, slate, terracotta, sage, charcoal, ink, copper.
const PALETTES = [
  { bg1: "#0b2a1f", bg2: "#1a4d36", accent: "#d4a268", ink: "#f7f3e9" }, // forest + gold
  { bg1: "#1a1a2e", bg2: "#2d2a52", accent: "#c4a373", ink: "#ede9d8" }, // ink + copper
  { bg1: "#2b1b3d", bg2: "#4a2f5c", accent: "#e0b85a", ink: "#f4e9d6" }, // plum + gold
  { bg1: "#0f2027", bg2: "#203a43", accent: "#92d5b8", ink: "#e7f5ee" }, // slate + sage
  { bg1: "#1c1c1e", bg2: "#3a3a3c", accent: "#d99b5e", ink: "#f1e7d5" }, // charcoal + terracotta
  { bg1: "#102a23", bg2: "#1f4d3f", accent: "#e8c987", ink: "#f3ecd6" }, // emerald + cream
  { bg1: "#2a1810", bg2: "#4a2d1c", accent: "#d4a268", ink: "#f1e3c9" }, // espresso + gold
  { bg1: "#0a1f2b", bg2: "#1d3a52", accent: "#7dd4a8", ink: "#e7f3ec" }, // midnight + mint
];

// Vector shapes for "package" silhouettes — chose by category.
// Each renders centered in a 400×320 viewBox.
const SHAPE = {
  // Jar with screw lid — typical eighth jar
  jar: ({ palette }) => (
    <>
      {/* lid */}
      <rect x="135" y="58" width="130" height="28" rx="4" fill={palette.accent} opacity="0.95" />
      <rect x="135" y="58" width="130" height="6" rx="2" fill="#000" opacity="0.18" />
      {/* jar body */}
      <rect x="120" y="86" width="160" height="200" rx="14" fill={palette.ink} opacity="0.07" stroke={palette.accent} strokeOpacity="0.45" strokeWidth="1.5" />
      {/* glass highlight */}
      <rect x="132" y="100" width="10" height="170" rx="5" fill={palette.ink} opacity="0.18" />
      {/* label */}
      <rect x="140" y="160" width="120" height="80" rx="6" fill={palette.bg1} opacity="0.92" stroke={palette.accent} strokeOpacity="0.6" strokeWidth="1" />
    </>
  ),
  // Mylar / eighth-pack pouch
  pouch: ({ palette }) => (
    <>
      {/* zipper tab */}
      <path d="M150 58 L250 58 L256 78 L144 78 Z" fill={palette.accent} opacity="0.9" />
      <line x1="150" y1="68" x2="250" y2="68" stroke="#000" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="3 3" />
      {/* bag body */}
      <path
        d="M138 78 Q120 80 122 110 L128 270 Q130 290 152 290 L248 290 Q270 290 272 270 L278 110 Q280 80 262 78 Z"
        fill={palette.bg1}
        stroke={palette.accent}
        strokeOpacity="0.55"
        strokeWidth="1.5"
      />
      {/* sheen */}
      <path d="M150 90 Q145 180 158 270" fill="none" stroke={palette.ink} strokeOpacity="0.18" strokeWidth="2" />
      {/* label band */}
      <rect x="135" y="170" width="130" height="70" rx="4" fill={palette.bg2} opacity="0.85" />
    </>
  ),
  // Vape cart box — tall rectangular box
  box: ({ palette }) => (
    <>
      <rect x="145" y="60" width="110" height="230" rx="6" fill={palette.bg1} stroke={palette.accent} strokeOpacity="0.55" strokeWidth="1.5" />
      {/* top fold */}
      <line x1="145" y1="78" x2="255" y2="78" stroke={palette.accent} strokeOpacity="0.4" strokeWidth="1" />
      {/* gold accent strip */}
      <rect x="145" y="130" width="110" height="5" fill={palette.accent} opacity="0.85" />
      {/* label window */}
      <rect x="158" y="150" width="84" height="120" rx="3" fill={palette.bg2} opacity="0.9" />
    </>
  ),
  // Edible / chocolate-bar style box (wider)
  bar: ({ palette }) => (
    <>
      <rect x="100" y="105" width="200" height="135" rx="8" fill={palette.bg1} stroke={palette.accent} strokeOpacity="0.55" strokeWidth="1.5" />
      <rect x="100" y="105" width="200" height="22" fill={palette.bg2} />
      <rect x="100" y="218" width="200" height="22" fill={palette.bg2} />
      <rect x="118" y="140" width="164" height="65" rx="4" fill={palette.bg2} opacity="0.85" />
    </>
  ),
};

const SHAPE_BY_CATEGORY = {
  flower: ["jar", "pouch", "jar", "pouch"],
  "pre-roll": ["box", "pouch"],
  "pre-rolls": ["box", "pouch"],
  vape: ["box"],
  vaporizers: ["box"],
  concentrate: ["jar", "pouch"],
  concentrates: ["jar", "pouch"],
  edible: ["bar", "box"],
  edibles: ["bar", "box"],
  tincture: ["box"],
  tinctures: ["box"],
  topical: ["box"],
  topicals: ["box"],
};

export const ProductPlaceholder = ({
  strainName = "",
  brand = "",
  category = "flower",
  className = "",
  testId,
}) => {
  const key = `${strainName}|${brand}|${category}`;
  const h = hashStr(key);
  const palette = PALETTES[h % PALETTES.length];
  const shapes = SHAPE_BY_CATEGORY[(category || "flower").toLowerCase()] || SHAPE_BY_CATEGORY.flower;
  const shapeKey = shapes[h % shapes.length];
  const Shape = SHAPE[shapeKey] || SHAPE.jar;

  // strain name on label — wrap to 2 lines if long
  const displayName = (strainName || "Premium Strain").replace(/\s+Flower$|\s+Cart(ridge)?$|\s+Gummies?$/i, "").trim();
  const labelText = displayName.length > 22 ? displayName.slice(0, 20) + "…" : displayName;
  const brandText = (brand || "Cannabis").toString().slice(0, 26).toUpperCase();

  // unique IDs per render to avoid SVG defs collisions
  const gid = `pp-grad-${h}`;
  const nid = `pp-noise-${h}`;

  return (
    <svg
      viewBox="0 0 400 320"
      xmlns="http://www.w3.org/2000/svg"
      className={`w-full h-full ${className}`}
      preserveAspectRatio="xMidYMid slice"
      data-testid={testId || `product-placeholder-${shapeKey}`}
      role="img"
      aria-label={`${brand || "Cannabis"} ${displayName} ${category} packaging illustration`}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={palette.bg1} />
          <stop offset="100%" stopColor={palette.bg2} />
        </linearGradient>
        {/* subtle film grain so the bg doesn't feel flat */}
        <pattern id={nid} x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="3" r="0.5" fill={palette.ink} opacity="0.04" />
          <circle cx="18" cy="11" r="0.4" fill={palette.ink} opacity="0.05" />
          <circle cx="32" cy="22" r="0.5" fill={palette.ink} opacity="0.04" />
          <circle cx="9" cy="28" r="0.4" fill={palette.ink} opacity="0.05" />
        </pattern>
      </defs>
      {/* background */}
      <rect width="400" height="320" fill={`url(#${gid})`} />
      <rect width="400" height="320" fill={`url(#${nid})`} />

      {/* the packaging shape */}
      <Shape palette={palette} />

      {/* Tiny strain identifier on the label — small, subtle, non-shouty.
          Previously displayed the strain name + brand at oversized sizes,
          which read as "AI slop" and pissed off the operator. Now it's
          just a small mark so the visual focus stays on the package shape. */}
      <g style={{ pointerEvents: "none" }} opacity="0.85">
        <text
          x="200"
          y={shapeKey === "bar" ? 184 : 196}
          textAnchor="middle"
          fontFamily="Inter, ui-sans-serif, system-ui"
          fontSize="9"
          fill={palette.accent}
          letterSpacing="3"
        >
          ◆
        </text>
        <text
          x="200"
          y={shapeKey === "bar" ? 200 : 214}
          textAnchor="middle"
          fontFamily="Inter, ui-sans-serif, system-ui"
          fontSize="8.5"
          fill={palette.ink}
          letterSpacing="0.6"
          opacity="0.8"
        >
          {labelText}
        </text>
      </g>
    </svg>
  );
};

export default ProductPlaceholder;
